import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { create } from "lodash";
import { ApiErrorPayload } from "types/ApiErrorPayload";


// #region types

export const ApplicationPlatform = {
  ipa: "iPhone/iPad (IPA)",
  apk: "Android (APK)",
  pwa: "Progresive Web Application (PWA)",
} as const;

export type APPLICATION_PLATFORM_KEYS = keyof typeof ApplicationPlatform;

export interface Application {
  id: number;
  title: string;
  geo: string;
  isActive: number;
}

export type ApplicationSlotData = {
  image: string;
  href: string;
};

type ApplicationDraftSlotData = {
  isModified: boolean;
  image: string | File;
  href: string;
  imageHref: string;
};

type ApplicationSlot = {
  [key in APPLICATION_PLATFORM_KEYS]: ApplicationSlotData;
};

export type ApplicationExtended = Application & {
  slots: ApplicationSlot;
};

type ApplicationDraftItem = Omit<ApplicationExtended, 'slots'> & {
  isModified: boolean;
  slots: {
    [key in keyof ApplicationSlot]: ApplicationDraftSlotData
  }
};

export interface ApplicationsState {
  list: Application[];
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: Application['id'][];
  processingItemId?: Application['id'];
  processingSlotId?: APPLICATION_PLATFORM_KEYS;
  activePage: number;
  totalItems: number;
  totalPages: number;
  draftItem: ApplicationDraftItem;
};

interface getApplicationsListPayload {
  limit?: number;
  offset?: number;
  title?: string;
  countries?: string[];
  isActive?: number;
  order?: Partial<Record<keyof Application, string>>
};

interface getApplicationsListResponse {
  items: Application[];
  totalItems: number;
  totalPages: number;
};

type CreateApplicationsItemPayload = Omit<Application, 'geo'> & {
  country: string;
  slots: {
    [key in keyof ApplicationSlot]?: Omit<ApplicationSlotData, 'image'> & { image?: File }
  }
};

type UpdateDraftSlotPayload = {
  id: APPLICATION_PLATFORM_KEYS;
} & Partial<{
  image: string | File;
  href: string;
}>;


const createDraftItem = (data: ApplicationExtended) => {
  const { slots, geo,...rest } = data;

  const slotsDraft = Object.keys(slots).reduce((acc, k) => {
    const key = k as APPLICATION_PLATFORM_KEYS;

    const platform = slots[key];

    acc[key] = {
      image: '',
      href: platform.href,
      imageHref: platform.image || '',
      isModified: false
    }

    return acc;
  }, {} as Record<APPLICATION_PLATFORM_KEYS, ApplicationDraftSlotData>);


  const draftItem: ApplicationDraftItem = {
    ...rest,
    geo: geo ?? 'WW',
    isModified: false,
    slots: slotsDraft
  }
  
  return draftItem;
};

const createInitialDraftItem = (): ApplicationDraftItem => {
  const primaryData: Omit<ApplicationDraftItem, 'slots'> = {
    id: 0,
    title: '',
    geo: 'WW',
    isActive: 0,
    isModified: false,
  };

  const slotsData = Object.keys(ApplicationPlatform).reduce((acc, k) => {
    const key = k as APPLICATION_PLATFORM_KEYS;

    acc[key] = {
      image: '',
      href: '',
      imageHref: '',
      isModified: false
    }

    return acc;
  }, {} as Record<APPLICATION_PLATFORM_KEYS, ApplicationDraftSlotData>);

  return {
    ...primaryData,
    slots: slotsData
  };
};

const createDataForUpdate = (data: CreateApplicationsItemPayload | Omit<CreateApplicationsItemPayload, 'id'>) => {
  const formData = new FormData();

  Object.keys(data).forEach((k) => {
    const key = k as keyof CreateApplicationsItemPayload | keyof Omit<CreateApplicationsItemPayload, 'id'>;

    if (key === 'id') return;

    if (key === 'slots') {
      const slots = data[key];

      Object.keys(slots).forEach((k) => {
        const key = k as APPLICATION_PLATFORM_KEYS;
        const href = slots[key]?.href || '';
        const image = slots[key]?.image || null;

        formData.append(`slots[${key}][href]`, href);

        if (image) {
          formData.append(`slots[${key}][image]`, image);
        }
      });

      return;
    };

    formData.append(key, `${data[key]}`);
  });

  return formData;
};
// #endregion helpers

// #region api
const bunchUpdateApplications = createAsyncThunk(
  "applications/bunch-update",
  async (data: { ids: number[]; isActive: number }) => {
    const result = await instance.put(apiRoutes.applicationsBunchUpdate, data);
    return result.data;
  }
);

const getApplicationsList = createAsyncThunk(
  "applications/list",
  async (data: getApplicationsListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.applicationsList}`, data);

      return result.data as getApplicationsListResponse;
    } catch (error) {
      throw error;
    }
  }
);

const deleteApplicationItem = createAsyncThunk(
  "applications/delete",
  async (id: number) => {
    try {
      await instance.delete(`${apiRoutes.applicationsDelete}/${id}`);
      return true;
    } catch (error) {
      throw error;
    }
  }
);

const getApplicationItem = createAsyncThunk(
  "applications/item",
  async (id: number) => {
    try {
      const result = await instance.get(`${apiRoutes.applicationsItem}/${id}`);

      return result.data as ApplicationExtended;
    } catch (error) {
      throw error;
    }
  }
);

const createApplicationItem = createAsyncThunk<
  ApplicationExtended,
  Omit<CreateApplicationsItemPayload, "id">,
  { rejectValue: ApiErrorPayload }
>("applications/create", async (data, { rejectWithValue }) => {
    try {
      const formData = createDataForUpdate(data);
      const result = await instance.post(
        `${apiRoutes.applicationsCreate}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return result.data as ApplicationExtended;
    } catch (error: any) {
      const apiError = error?.data?.error;
      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const updateApplicationItem = createAsyncThunk<
  ApplicationExtended,
  CreateApplicationsItemPayload,
  { rejectValue: ApiErrorPayload }
>("applications/update", async (data, { rejectWithValue }) => {
  try {
    const formData = createDataForUpdate(data);
    const result = await instance.put(
      `${apiRoutes.applicationsUpdate}/${data.id}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return result.data as ApplicationExtended;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});


// #endregion api

const initialState: ApplicationsState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 1,
  selectedItems: [],
  draftItem: createInitialDraftItem()
};


const applicationsSlice = createSlice({
  name: "cms-applications",
  initialState,
  reducers: {
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleSelectAll: (state) => {
      state.selectedItems =
        state.selectedItems.length !== state.list.length
          ? state.list.map((item) => item.id)
          : [];
    },
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleClearDraftItem: (state) => {
      state.draftItem = createInitialDraftItem();
      state.isItemFetched = false;
    },
    handleUpdateDraftItem: (
      state,
      action: PayloadAction<Partial<Omit<ApplicationDraftItem, "slots">>>,
    ) => {
      state.draftItem = {
        ...state.draftItem,
        ...action.payload,
        isModified: true,
      };
    },
    handleUpdateDraftItemSlot: (
      state,
      action: PayloadAction<UpdateDraftSlotPayload>,
    ) => {
      const { id, ...restPayload } = action.payload;
      const updatedSlot: ApplicationDraftSlotData = {
        ...state.draftItem.slots[action.payload.id],
        ...restPayload,
        isModified: true,
      };
      state.draftItem.slots = {
        ...state.draftItem.slots,
        [action.payload.id]: {
          ...updatedSlot,
        },
      };
    },
    handleClearSlot: (
      state,
      action: PayloadAction<APPLICATION_PLATFORM_KEYS>,
    ) => {
      const clearedSlotData = createInitialDraftItem().slots[action.payload];

      state.draftItem = {
        ...state.draftItem,
        isModified: true,
        slots: {
          ...state.draftItem.slots,
          [action.payload]: clearedSlotData,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApplicationsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(deleteApplicationItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(deleteApplicationItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteApplicationItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(getApplicationItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      const draftItem = createDraftItem(action.payload);
      state.draftItem = draftItem;
    });
    builder.addCase(createApplicationItem.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createApplicationItem.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createApplicationItem.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateApplicationItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateApplicationItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.payload.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = undefined;
      const draftItem = createDraftItem(action.payload);
      state.draftItem = draftItem;
    });
    builder.addCase(updateApplicationItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(bunchUpdateApplications.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  },
});

const {
  handleSelect,
  handleSelectAll,
  handlePaginate,
  handleClearDraftItem,
  handleUpdateDraftItem,
  handleClearSlot,
  handleUpdateDraftItemSlot
} = applicationsSlice.actions;

export {
  getApplicationsList,
  applicationsSlice,
  handleSelect,
  handleSelectAll,
  handlePaginate,
  deleteApplicationItem,
  getApplicationItem,
  handleClearDraftItem,
  handleUpdateDraftItem,
  handleClearSlot,
  handleUpdateDraftItemSlot,
  createApplicationItem,
  updateApplicationItem,
  bunchUpdateApplications,
};
